/**
 * Segment colors for a room, assigned like a chart's series: by order.
 *
 * The room's segment order is built once by walking its twins in order and
 * appending each segment name the first time it appears (each twin's names
 * already arrive in the audience's saved order). A segment's slot is its
 * position in that order, wrapping after 7, so the first segment gets slot 1
 * and a segment wears the same color on every card in the room.
 *
 * Slots map to the plot palette (`--slide-chart-1..7`); slot 8 is the
 * palette's neutral grey and is never used. Brand themes write
 * `--slide-chart-N` on `:root` at runtime, but deck-ui's `tokens.css` is not
 * loaded app-wide, so each slot falls back to one of the app's hue-separated,
 * light/dark aware chart tokens (`globals.css`).
 */

const SEGMENT_SLOT_FALLBACKS: Array<string> = [
  'hsl(var(--chart-primary))',
  'hsl(var(--chart-accent))',
  'hsl(var(--chart-tertiary))',
  'hsl(var(--chart-quinary))',
  'hsl(var(--chart-warning))',
  'hsl(var(--chart-secondary))',
  'hsl(var(--chart-senary))',
]

export const SEGMENT_SLOT_COUNT = SEGMENT_SLOT_FALLBACKS.length

/** Each segment name's palette slot (1-7), in first-seen order across the room's twins. */
export function getSegmentColorSlots(participantsTagNames: Array<Array<string> | undefined>): Map<string, number> {
  const slots = new Map<string, number>()

  for (const tagNames of participantsTagNames) {
    for (const rawName of tagNames ?? []) {
      const name = rawName.trim()
      if (name && !slots.has(name)) {
        slots.set(name, (slots.size % SEGMENT_SLOT_COUNT) + 1)
      }
    }
  }

  return slots
}

/** A slot's color as a CSS value, e.g. `var(--slide-chart-1, hsl(var(--chart-primary)))`. */
export function segmentColor(slot: number): string {
  const safeSlot = ((Math.max(1, Math.floor(slot)) - 1) % SEGMENT_SLOT_COUNT) + 1
  return `var(--slide-chart-${safeSlot}, ${SEGMENT_SLOT_FALLBACKS[safeSlot - 1]})`
}
